import type { CSSProperties, ReactNode } from "react";
import { m, useReducedMotion } from "motion/react";
import { NavLink, useNavigate } from "react-router-dom";
import { Button } from "../Button";
import { clearTeamApiState, teamMutation } from "../../lib/teamApi";

type Props = {
  companyName: string;
  organiserName: string;
  avatarUrl?: string | null;
  logoUrl?: string | null;
  colours?: { primary: string; secondary: string; accent: string };
  children: ReactNode;
};

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

export function WorkspaceShell({ companyName, organiserName, avatarUrl, logoUrl, colours, children }: Props) {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const style = colours ? { "--company-primary": colours.primary, "--company-secondary": colours.secondary, "--company-accent": colours.accent } as CSSProperties : undefined;
  return <div className="workspace-page" style={style}>
    <header className="workspace-topbar">
      <NavLink className="workspace-company" to="/team/workspace" aria-label={`${companyName} workspace home`}>
        {logoUrl ? <img src={logoUrl} alt="" /> : <span className="workspace-company-mark" aria-hidden="true">{initials(companyName)}</span>}
        <span><strong>{companyName}</strong><small>Friction Finder · Powered by HuddleCo</small></span>
      </NavLink>
      <div className="workspace-contact"><span className="profile-avatar">{avatarUrl ? <img src={avatarUrl} alt="" /> : initials(organiserName)}</span><span>{organiserName}</span></div>
    </header>
    <div className="workspace-frame">
      <nav className="workspace-nav" aria-label="Company Workspace">
        <NavLink end to="/team/workspace">Workspace</NavLink>
        <NavLink to="/team/workspace/profile">Profile</NavLink>
        <NavLink to="/team/workspace/branding">Branding</NavLink>
        <Button variant="text" onClick={async () => { try { await teamMutation("organiser", "/api/team/organiser/logout"); } finally { clearTeamApiState(); navigate("/team", { replace: true }); } }}>Sign out</Button>
        <p className="powered-by">Powered by HuddleCo</p>
      </nav>
      <m.main className="workspace-main" data-motion-region="workspace" initial={reduceMotion ? false : { opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduceMotion ? 0 : 0.16, ease: [0.22, 1, 0.36, 1] }}>{children}</m.main>
    </div>
  </div>;
}
